import React, { useEffect, useRef, useState } from 'react';

import { errors, isValid, validator } from 'react-validator';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { TeacherAssignmentTypeEnum } from '@school_ify/schoolify-repository/src/teacher/types';
import {
  Chats,
  useAssignmentGradeWeightsQuery,
  useChatByTypeGroupQuery,
  FileFragment,
} from '@school_ify/schoolify-repository/src/shared/types';

import { useAppSelector } from 'redux/hooks';
import useEffectAfterMount from 'hooks/useEffectAfterMount';
import TeachingStructureDropDown from 'components/TeachingStructure/TeachingStructureDropDown';
import ClassDropDown from 'components/Header/components/ClassDropDown/ClassDropDown';
import Errors from 'components/Errors/Errors';
import Editor from 'components/TestEditor/Editor';
import { liveClassStudentsState } from 'redux/liveClass/students.slices';
import useSaveStudy from 'views/Study/components/Forms/useSaveStudy';
import { IModel } from 'utils/IStudy';
import DatePicker from 'components/Input/DatePicker/DatePicker';
import FileUploader from 'clean/components/common/FileUploader/FileUploader';

import Students from '../../../LiveClass/components/TeacherDashboard/components/students/Index';

import '../Pages/StudyEdit.scoped.scss';

const Task: React.FC<{
  children?: React.ReactNode;
  subject: string;
  data: IModel;
  assignmentFiles: FileFragment[];
  chat?: Chats;
  saveEvent?: any; // It really can be anything
  renderWithoutActions?: boolean;
  title: string;
  actionsCentered?: boolean;
  gradeWeight?: any;
  onCancel?: () => void;
  onSave?: (subject: string, assignmentId: string, title: string) => void;
}> = ({
  subject,
  data,
  assignmentFiles,
  chat,
  title,
  children,
  actionsCentered = false,
  saveEvent = null,
  renderWithoutActions = false,
  gradeWeight,
  onCancel,
  onSave,
}) => {
  const [validation, setValidation] = useState({});
  const { t } = useTranslation();
  const { selectedStudents } = useAppSelector(liveClassStudentsState);
  const { saveStudy, setFiles, files } = useSaveStudy();
  const descriptionErrorRef = useRef(null);
  const assignmentGradeErrorRef = useRef(null);
  const teachingStructureErrorRef = useRef(null);
  const titleErrorRef = useRef(null);
  const selectedStudentsErrorRef = useRef(null);

  const [model, setModel] = useState({
    ...data,
    type: TeacherAssignmentTypeEnum.TASK,
  });

  // chat
  const chatData = useChatByTypeGroupQuery();

  const [selectedChat, setSelectedChat] = useState({
    title: t('common:SelectChat'),
    id: null,
  });
  // grade weight

  const { data: gradeWeightData, loading: gradesWeightLoading } = useAssignmentGradeWeightsQuery();

  const [selectedGradeWeight, setSelectedGradeWeight] = useState({
    title: t('common:SelectGradeWeight'),
    id: null,
  });

  function setModelProp(e, prop) {
    const copyMod = { ...model };
    copyMod[prop] = e;
    setModel(copyMod);
  }

  const scrollToError = ref => {
    ref.current.scrollIntoView({
      behavior: 'smooth',
    });
  };

  async function save() {
    validator(
      { model, selected_students: selectedStudents },
      {
        'model.title': ['required', 'string'],
        'model.duration': ['required', 'integer', 'between:1,1000'],
        'model.description': ['required'],
        'model.teaching_structure_id': ['required', 'string'],
        'model.due_at': ['required'],
        'model.assignment_grade_weight_id': ['required'],
        selected_students: ['min: 1'],
      },
      {
        'model.title.required': t('study:title_required'),
        'model.duration.required': t('study:duration_required'), //
        'model.duration.between': t('study:duration_between'),
        'model.description.required': t('study:description_required'), //
        'model.teaching_structure_id.required': t('study:structure_id_required'),
        'selected_students.min': t('study:students_min'),
        'model.assignment_grade_weight_id.required': t('study:assignment_grade_weight_id_required'),
      }
    );
    setValidation(errors());
    if (!isValid()) {
      return;
    }
    const assignmentId = await saveStudy(model);
    onSave && onSave(subject, assignmentId, title);
  }

  useEffect(() => {
    if (validation['selected_students']) {
      scrollToError(selectedStudentsErrorRef);
    }
    if (validation['model.description']) {
      scrollToError(descriptionErrorRef);
    }
    if (validation['model.assignment_grade_weight_id']) {
      scrollToError(assignmentGradeErrorRef);
    }
    if (validation['model.teaching_structure_id']) {
      scrollToError(teachingStructureErrorRef);
    }
    if (validation['model.title']) {
      scrollToError(titleErrorRef);
    }
  }, [validation]);

  useEffect(() => {
    if (assignmentFiles) {
      setFiles(
        assignmentFiles.map(file => {
          const dummyFile = new File([''], file.file_name);
          Object.defineProperty(dummyFile, 'id', {
            value: file.id,
            writable: false,
          });
          return dummyFile;
        })
      );
    }
  }, [assignmentFiles]);

  function printChats() {
    return chatData.data ? (
      chatData.data.chats.map(chat => {
        return (
          <div
            key={chat.id}
            className="pointer"
            onClick={() => {
              setSelectedChat({
                id: chat.id,
                title: chat.title,
              });
              model.chat_id = chat.id;
            }}
          >
            {chat.title}
          </div>
        );
      })
    ) : (
      <div>No chats found</div>
    );
  }

  const handleGradesWeightOnClick = gradeWeight => {
    setSelectedGradeWeight({
      id: gradeWeight.id,
      title: gradeWeight.value,
    });
    model.assignment_grade_weight_id = gradeWeight.id;
  };

  function printGradeWeights() {
    if (gradesWeightLoading) {
      return <div>{t('study:No_grade_weight_found')}</div>;
    }

    return gradeWeightData.assignment_grade_weights.map(gradeWeight => {
      return (
        <div
          key={gradeWeight.id}
          className="pointer"
          onClick={() => handleGradesWeightOnClick(gradeWeight)}
        >
          {gradeWeight.value}
        </div>
      );
    });
  }

  useEffect(() => {
    if (selectedChat.id) {
      model.chat_id = selectedChat.id;
    }
  }, [selectedChat]);

  useEffectAfterMount(() => {
    save();
  }, [saveEvent]);

  useEffect(() => {
    setModel({
      ...data,
      chat_id: chat?.id ? chat.id : null,
      type: TeacherAssignmentTypeEnum.TASK,
    });
  }, [data]);

  useEffect(() => {
    if (chat) {
      setSelectedChat({
        id: chat.id,
        title: chat.title,
      });
    }
  }, [chat]);

  useEffect(() => {
    if (gradeWeight) {
      setSelectedGradeWeight({
        id: gradeWeight.id,
        title: gradeWeight.value,
      });
    }
  }, [gradeWeight]);

  return (
    <>
      <div ref={titleErrorRef} />
      <div className="grid grid-col-2 sm-grid-col-1 gap-1">
        <div>
          <label htmlFor="title">{t('study:title')}</label>
          <input
            type="text"
            className="full-input"
            value={model.title}
            onChange={e => {
              setModelProp(e.target.value, 'title');
            }}
            placeholder={t('study:title').toString().replace('*', '')}
            id={'title'}
          />
          <Errors errors={validation} prop={'model.title'} />
        </div>
        <div>
          <div ref={teachingStructureErrorRef} />
          <label htmlFor="title">{t('study:chapter')}</label>
          <div style={{ display: 'block' }}>
            {subject && (
              <>
                <TeachingStructureDropDown
                  key={model.teaching_structure_id}
                  teaching_structure_id={subject}
                  selected={model.teaching_structure_id}
                  onSelect={val => {
                    setModelProp(val.id, 'teaching_structure_id');
                  }}
                />
              </>
            )}
          </div>
          <div className="mt-1">
            <Errors prop={'model.teaching_structure_id'} errors={validation} />
          </div>
        </div>
        <div>
          <label htmlFor="due_at">{t('study:due_at')}:</label>
          <DatePicker
            value={model.due_at}
            onChange={({ dateStr }) => {
              setModelProp(dateStr, 'due_at');
            }}
          />
          <div className="mb-1">
            <Errors prop={'model.due_at'} errors={validation} />
          </div>
        </div>
        <div>
          <label htmlFor="Duration">{t('study:duration')}</label>
          <input
            type="number"
            className="full-input"
            value={model.duration}
            placeholder={t('study:duration_placeholder')}
            onChange={e => {
              setModelProp(e.target.value, 'duration');
            }}
            id={'Duration'}
          />
          <Errors errors={validation} prop={'model.duration'} />
        </div>
      </div>
      <div className="flex gap-1 mb-1">
        <div className="w-100">
          <label htmlFor="subject">{t('common:SelectChat')}</label>
          <ClassDropDown key={selectedChat.id} value={selectedChat.title}>
            {printChats()}
          </ClassDropDown>
        </div>
        <div ref={assignmentGradeErrorRef} />
        <div className="w-100">
          <label htmlFor="subject">{t('common:SelectGradeWeight')}</label>
          <ClassDropDown key={selectedGradeWeight.id} value={selectedGradeWeight.title}>
            {printGradeWeights()}
          </ClassDropDown>
          <Errors errors={validation} prop={'model.assignment_grade_weight_id'} />
        </div>
      </div>
      <div ref={descriptionErrorRef} />
      <div className="mt-1 mb-1">
        <label htmlFor="content">{t('study:Content')}</label>
        <Editor value={model.description} callback={val => setModelProp(val, 'description')} />
        <Errors errors={validation} prop={'model.description'} />
      </div>
      <div className="mt-1 mb-1">
        <label htmlFor="content">{t('study:Additional_documents')}</label>
        <FileUploader readOnly={false} files={files} setFiles={setFiles} />
      </div>
      <div className="mb-1">
        <Students />
        <div ref={selectedStudentsErrorRef} />
        <Errors errors={validation} prop={'selected_students'} />
      </div>
      <div className="text-small text text-red">
        {Object.keys(validation).length > 0 && (
          <div className="text-red">{t('common:there_are_some_error_please_check')}</div>
        )}
      </div>
      <div className={classNames('task-bttn-container', { actionsCentered })}>
        {!renderWithoutActions && (
          <div className="flex mt-2 justify-center">
            <label
              className="cancel-btn btn rounded-full nowrap no-underline text-bold mr-1"
              onClick={onCancel}
            >
              Cancel
            </label>
            <button
              onClick={save}
              style={{ fontSize: '16px', border: 'none' }}
              className="flex justify-center align-center btn nowrap shadow w-50 rounded-full bg-primary text-white no-underline text-bold"
            >
              {t('common:save')}
            </button>
          </div>
        )}
        {children}
      </div>
    </>
  );
};

export default Task;
